import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, Plus, Mic, Calendar, Clock, FileText } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock data
const MOCK_MEETINGS = [
  {
    id: "mtg-001",
    title: "Whole Foods Q1 Planning Call",
    meeting_date: "2025-01-10T14:00:00",
    duration_minutes: 45,
    participants: ["John Doe", "Sarah Johnson (Whole Foods)", "Mike Chen"],
    opportunity_id: "opp-001",
    opportunity_name: "Whole Foods Market",
    summary: "Discussed Q1 distribution expansion plans. Whole Foods interested in adding 3 new SKUs to their organic frozen section. Key action items: send updated pricing by EOW, schedule follow-up with regional buyers.",
    transcript: "Full transcript of the meeting discussion...",
    action_items_generated: 2,
    source: "manual"
  },
  {
    id: "mtg-002",
    title: "Costco Product Demo",
    meeting_date: "2025-01-08T10:00:00",
    duration_minutes: 60,
    participants: ["Jane Smith", "Bob Wilson (Costco)"],
    opportunity_id: "opp-002",
    opportunity_name: "Costco Wholesale",
    summary: "Product demonstration at Costco regional office. Buyer was impressed with the new club pack sizes. They want to run a test in 50 locations before national rollout.",
    transcript: null,
    action_items_generated: 3,
    source: "fireflies"
  },
  {
    id: "mtg-003",
    title: "Internal Sales Strategy",
    meeting_date: "2025-01-05T09:00:00",
    duration_minutes: 30,
    participants: ["John Doe", "Jane Smith", "Sales Team"],
    opportunity_id: null,
    opportunity_name: null,
    summary: "Weekly sales team sync. Reviewed pipeline status and Q1 targets. Team is on track to exceed targets by 15%.",
    transcript: null,
    action_items_generated: 0,
    source: "manual"
  }
];

const MeetingsPage = ({ navigateToOpportunity }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    meeting_date: "",
    duration_minutes: 30,
    participants: "",
    summary: ""
  });

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/crm/meetings`);
      setMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings', error);
      setMeetings(MOCK_MEETINGS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async () => {
    try {
      const payload = {
        ...newMeeting,
        participants: newMeeting.participants.split(',').map(p => p.trim()).filter(p => p)
      };
      await axios.post(`${API}/crm/meetings`, payload);
      toast.success("Meeting recorded");
      setShowCreateModal(false);
      setNewMeeting({ title: "", meeting_date: "", duration_minutes: 30, participants: "", summary: "" });
      fetchMeetings();
    } catch (error) {
      console.error('Failed to create meeting', error);
      toast.error("Failed to create meeting");
    }
  };

  const handleViewMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailModal(true);
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'fireflies': return <Badge className="bg-purple-100 text-purple-800">🔥 Fireflies</Badge>;
      case 'otter': return <Badge className="bg-blue-100 text-blue-800">🦦 Otter</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-800">✏️ Manual</Badge>;
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const filteredMeetings = meetings.filter(meeting => {
    return searchQuery === "" || 
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.opportunity_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Sort by date, newest first
  const sortedMeetings = [...filteredMeetings].sort((a, b) => 
    new Date(b.meeting_date) - new Date(a.meeting_date)
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Meetings</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mic className="w-6 h-6" />
          Meetings
        </h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Meeting
        </Button>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-indigo-50 border-indigo-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h3 className="font-semibold text-indigo-900">AI Meeting Integration</h3>
              <p className="text-sm text-indigo-700">
                Connect Fireflies.ai or Otter.ai to automatically capture meeting transcripts, 
                generate summaries, and create action items from your sales calls.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search meetings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Meetings List */}
      <div className="space-y-3">
        {sortedMeetings.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No meetings found
            </CardContent>
          </Card>
        ) : (
          sortedMeetings.map(meeting => (
            <Card 
              key={meeting.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleViewMeeting(meeting)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{meeting.title}</h3>
                      {getSourceBadge(meeting.source)}
                    </div>
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">{meeting.summary}</p>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(meeting.meeting_date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(meeting.duration_minutes)}
                      </span>
                      <span>👥 {meeting.participants?.length || 0} participants</span>
                      {meeting.opportunity_name && (
                        <span 
                          className="cursor-pointer hover:text-indigo-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToOpportunity && navigateToOpportunity(meeting.opportunity_id);
                          }}
                        >
                          🎯 {meeting.opportunity_name}
                        </span>
                      )}
                      {meeting.action_items_generated > 0 && (
                        <span className="text-green-600">✓ {meeting.action_items_generated} action items</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Meeting Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Meeting</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                placeholder="Meeting title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={newMeeting.meeting_date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, meeting_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={newMeeting.duration_minutes}
                  onChange={(e) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(e.target.value) || 30 })}
                />
              </div>
            </div>
            <div>
              <Label>Participants</Label>
              <Input
                value={newMeeting.participants}
                onChange={(e) => setNewMeeting({ ...newMeeting, participants: e.target.value })}
                placeholder="Comma-separated names"
              />
            </div>
            <div>
              <Label>Summary / Notes</Label>
              <Textarea
                value={newMeeting.summary}
                onChange={(e) => setNewMeeting({ ...newMeeting, summary: e.target.value })}
                placeholder="Key points and outcomes..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateMeeting} disabled={!newMeeting.title || !newMeeting.meeting_date}>
              Save Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedMeeting && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle>{selectedMeeting.title}</DialogTitle>
                  {getSourceBadge(selectedMeeting.source)}
                </div>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Meta info */}
                <div className="flex gap-4 text-sm text-gray-500 border-b pb-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedMeeting.meeting_date).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDuration(selectedMeeting.duration_minutes)}
                  </span>
                  {selectedMeeting.opportunity_name && (
                    <span>🎯 {selectedMeeting.opportunity_name}</span>
                  )}
                </div>

                {/* Participants */}
                <div>
                  <h4 className="font-semibold mb-2">Participants</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMeeting.participants?.map((p, i) => (
                      <Badge key={i} variant="outline">{p}</Badge>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <h4 className="font-semibold mb-2">Summary</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMeeting.summary || 'No summary available'}</p>
                </div>

                {/* Transcript */}
                {selectedMeeting.transcript && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Transcript
                    </h4>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm max-h-48 overflow-y-auto">
                      {selectedMeeting.transcript}
                    </div>
                  </div>
                )}

                {/* Action Items */}
                {selectedMeeting.action_items_generated > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <span className="text-green-700 font-medium">
                      ✓ {selectedMeeting.action_items_generated} action items were generated from this meeting
                    </span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingsPage;
